/**
 * A representative's own customers.
 *
 * It reads `GET /me/portfolio`, which resolves the representative from the
 * authenticated account. That is the whole reason this screen exists next to
 * the one an administrator uses: asking for somebody else's portfolio by
 * passing their code is not expressible here.
 *
 * Ordered by what is owed, because the screen exists so a representative can
 * see who to chase and alphabetical order buries that under the customers who
 * owe nothing. The order is the server's; this screen does not re-sort.
 */

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import { useResource } from "@hardauto/core-data";
import { money } from "@hardauto/core-i18n";
import {
  Alert,
  DataTable,
  Meter,
  Page,
  Stat,
  StatGrid,
  StatusDot,
  type Column,
} from "@hardauto/core-ui";

import { ClientsApi, type PortfolioEntry } from "../api";
import { creditTone } from "../credit";
import { NAMESPACE } from "../i18n";
import { CLIENTS_PATHS } from "../paths";
import "./clients.css";

export function PortfolioScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ClientsApi(client), [client]);

  const fetcher = useCallback(() => api.myPortfolio(), [api]);
  const portfolio = useResource(fetcher);

  const columns: Column<PortfolioEntry>[] = [
    {
      key: "code",
      header: t("portfolio.column.code"),
      render: (row) => (
        <Link to={CLIENTS_PATHS.client.replace("$id", row.client.id)}>
          <span className="hardauto-ref">{row.client.code}</span>
        </Link>
      ),
    },
    {
      key: "company",
      header: t("portfolio.column.company"),
      render: (row) => row.client.company_name,
    },
    { key: "city", header: t("portfolio.column.city"), render: (row) => row.client.city || "—" },
    {
      key: "usage",
      header: t("portfolio.column.usage"),
      render: (row) => (
        <Meter
          value={row.client.credit_usage_pct}
          text={money(row.client.credit_used, i18n.language)}
          label={t("client.credit.usage")}
          tone={creditTone(row.client.credit_usage_pct, row.client.is_over_credit_limit)}
        />
      ),
    },
    {
      key: "available",
      header: t("portfolio.column.available"),
      render: (row) => (
        <span className="hardauto-num">{money(row.client.credit_available, i18n.language)}</span>
      ),
    },
    {
      key: "access",
      header: t("portfolio.column.access"),
      render: (row) => (
        <StatusDot
          label={t(`client.status.${row.client.portal_status}`)}
          tone={
            row.client.portal_status === "active"
              ? "success"
              : row.client.portal_status === "suspended"
                ? "danger"
                : "neutral"
          }
        />
      ),
    },
  ];

  // A 404 here means "this account is not a representative", which is a
  // configuration fact rather than a failure — telling somebody the page is
  // broken would send them to the wrong person.
  const notLinked = portfolio.failed && portfolio.data === null;

  return (
    <Page title={t("portfolio.title")} description={t("portfolio.intro")}>
      {notLinked ? <Alert tone="info">{t("portfolio.notLinked")}</Alert> : null}

      {portfolio.data ? (
        <StatGrid label={t("portfolio.title")}>
          <Stat label={t("portfolio.stat.clients")} value={portfolio.data.items.length} />
          <Stat
            label={t("portfolio.stat.limit")}
            value={money(portfolio.data.total_credit_limit, i18n.language)}
          />
          <Stat
            label={t("portfolio.stat.used")}
            value={money(portfolio.data.total_credit_used, i18n.language)}
          />
          <Stat
            label={t("portfolio.stat.over")}
            value={portfolio.data.clients_over_limit}
            tone={portfolio.data.clients_over_limit > 0 ? "danger" : "neutral"}
          />
        </StatGrid>
      ) : null}

      <DataTable<PortfolioEntry>
        caption={t("portfolio.caption")}
        columns={columns}
        rows={portfolio.data?.items ?? []}
        rowKey={(row) => row.client.id}
        loading={portfolio.loading}
        loadingLabel={t("portfolio.loading")}
        empty={{ title: t("portfolio.empty.title"), description: t("portfolio.empty.body") }}
      />
    </Page>
  );
}
